import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";

import { findInsertionNeighbours, type PositionableEvent } from "../lib/timeline-insertion";

interface EventInsertionPreviewProps {
  /** The claim's existing events — `claim.claimEvents`, already loaded by the modal. */
  events: PositionableEvent[];
  /** The composed event date, or `undefined` for "maintenant". */
  eventDate: Date | undefined;
  /** Label of the event about to be sent. */
  machineEvent: string;
}

function EventRow({ label, date, isNew }: { label: string; date: Date; isNew?: boolean }) {
  return (
    <li className={cn("flex items-baseline gap-2", isNew && "font-semibold text-gray-900")}>
      <span aria-hidden="true" className={cn("text-gray-400", isNew && "text-amber-600")}>
        {isNew ? "▸" : "●"}
      </span>
      <span className="flex-1 truncate">{label}</span>
      <span className="shrink-0 tabular-nums text-gray-500">
        {format(date, "d MMM, HH:mm", { locale: fr })}
      </span>
    </li>
  );
}

/**
 * Shows the two events the pending one will land between, so a backdated `eventDate` stops
 * being an invisible decision.
 *
 * This replaces the silent backend clamp from TECH-892: an event dated before the last one is
 * flagged in amber, but the admin can still confirm — jurists legitimately backdate an event to
 * when a letter was actually signed, and the software should check they meant it rather than
 * quietly overrule them.
 *
 * Ordered newest-first to match the timeline itself.
 */
export function EventInsertionPreview({ events, eventDate, machineEvent }: EventInsertionPreviewProps) {
  // `undefined` means the API will stamp `now()`, so preview it at now.
  const target = eventDate ?? new Date();
  const { before, after, landsBeforeLast } = findInsertionNeighbours(events, target);

  if (!before && !after) {
    return <p className="mt-2 text-xs text-gray-500">Premier évènement de la timeline.</p>;
  }

  return (
    <div
      className={cn(
        "mt-2 rounded-lg border p-3 text-xs",
        landsBeforeLast ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-gray-50",
      )}
    >
      <p className="mb-2 font-medium text-gray-700">Position dans la timeline</p>

      <ul className="space-y-1 text-gray-600">
        {after ? <EventRow label={after.eventType.displayName} date={new Date(after.eventDate)} /> : null}
        <EventRow label={machineEvent} date={target} isNew />
        {before ? <EventRow label={before.eventType.displayName} date={new Date(before.eventDate)} /> : null}
      </ul>

      {landsBeforeLast ? (
        <p className="mt-3 flex items-start gap-1.5 text-amber-800">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>Cet évènement sera inséré avant le dernier évènement de la timeline.</span>
        </p>
      ) : null}
    </div>
  );
}
